import { useEffect, useState } from "react";
import { BACKEND_URL } from "../../lib/api";

const BOX_W = 1600;
const BOX_H = 900;

// Homography taking the BOX_W×BOX_H box onto a four-corner quad (TL, TR, BR, BL), as a CSS matrix3d.
const toMatrix3d = (q) => {
  const src = [[0, 0], [BOX_W, 0], [BOX_W, BOX_H], [0, BOX_H]];
  const A = [];
  const b = [];
  src.forEach(([x, y], i) => {
    const [u, v] = q[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  });
  for (let c = 0; c < 8; c++) {   // Gaussian elimination
    let p = c;
    for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    [A[c], A[p]] = [A[p], A[c]]; [b[c], b[p]] = [b[p], b[c]];
    for (let r = 0; r < 8; r++) {
      if (r === c) continue;
      const f = A[r][c] / A[c][c];
      for (let k = c; k < 8; k++) A[r][k] -= f * A[c][k];
      b[r] -= f * b[c];
    }
  }
  const h = b.map((v, i) => v / A[i][i]);
  return `matrix3d(${h[0]},${h[3]},0,${h[6]},${h[1]},${h[4]},0,${h[7]},0,0,1,0,${h[2]},${h[5]},0,1)`;
};

const pct = (s, i) => {
  const part = (s || "center 22%").split(" ")[i] || "center";
  if (part === "center") return 0.5;
  if (part === "top" || part === "left") return 0;
  if (part === "bottom" || part === "right") return 1;
  return parseFloat(part) / 100;
};

// Image pixel → screen pixel for a background drawn with `cover` at the club's background position.
const useCover = (club) => {
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const [iw, ih] = club.bgDims || [3792, 2544];
  const s = Math.max(vp.w / iw, vp.h / ih);
  const ox = (vp.w - iw * s) * pct(club.bgPos, 0);
  const oy = (vp.h - ih * s) * pct(club.bgPos, 1);
  return (pts) => pts.map(([x, y]) => [ox + x * s, oy + y * s]);
};

// The club's uploaded pictures, rotating on the TVs painted into their room. The logo slot shows the room as painted.
export const ClubTvScreens = ({ club, screens }) => {
  const map = useCover(club);
  const set = screens?.settings || {};
  const pics = (screens?.tv || []).map((p) => `${BACKEND_URL}${p.url}`);
  const slides = [...(set.tv_logo !== false ? [null] : []), ...pics];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setI((n) => (n + 1) % slides.length), (set.seconds || 10) * 1000);
    return () => clearInterval(id);
  }, [slides.length, set.seconds]);
  if (!club.tvs?.length || set.tv_on === false || !pics.length) return null;
  const cur = slides[i % slides.length];
  return (
    <div aria-hidden data-testid="club-tv-screens" className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
      {club.tvs.map((q, n) => (
        <div key={n} data-testid={`club-tv-${n}`} className="absolute left-0 top-0 overflow-hidden"
          style={{ width: BOX_W, height: BOX_H, transformOrigin: "0 0", transform: toMatrix3d(map(q)) }}>
          {pics.map((src) => (
            <img key={src} src={src} alt="" className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: cur === src ? 1 : 0, transition: "opacity 900ms ease" }} />
          ))}
        </div>
      ))}
    </div>
  );
};
